import {
  ArrowRight,
  Bot,
  Boxes,
  Check,
  Code2,
  Cpu,
  Database,
  FlaskConical,
  Lightbulb,
  ListChecks,
  Menu,
  MessageCircle,
  Sparkles,
  X,
} from 'lucide-react';
import { motion } from 'motion/react';
import { type FormEvent, type ReactNode, useState } from 'react';

const services = [
  [Sparkles, 'Experiential Marketing', 'Brand activations and digital experiences designed to be remembered, shared and measured.'],
  [Code2, 'Web Development', 'Elegant, high-performance websites and applications engineered around real business outcomes.'],
  [Cpu, 'IoT Applications & Devices', 'Connected devices, dashboards, embedded integrations and real-time systems from prototype to deployment.'],
  [Database, 'Data Engineering & Analytics', 'Reliable pipelines, analytics layers and decision-ready dashboards for modern businesses.'],
  [Bot, 'AI Solutions & Chatbots', 'Practical AI assistants, RAG experiences, workflow automation and intelligent applications.'],
  [Boxes, 'Product Development', 'Discovery, architecture, prototyping, engineering and launch support for digital and connected products.'],
  [ListChecks, 'Project Management', 'Agile planning, delivery governance and cross-functional coordination for complex initiatives.'],
  [FlaskConical, 'R&D for Startups', 'Technical research, feasibility studies, proof-of-concepts and prototypes for ambitious ideas.'],
  [Lightbulb, 'Startup Consulting', 'Product thinking, technology strategy and hands-on guidance from validation to market readiness.'],
] as const;

const clients = ['Gullak', 'EscapeTechSolutions', 'Innotronics'];

const countries = [
  'Afghanistan', 'Albania', 'Algeria', 'Andorra', 'Angola', 'Argentina', 'Armenia', 'Australia', 'Austria', 'Azerbaijan',
  'Bahamas', 'Bahrain', 'Bangladesh', 'Barbados', 'Belarus', 'Belgium', 'Belize', 'Bhutan', 'Bolivia', 'Bosnia and Herzegovina',
  'Botswana', 'Brazil', 'Brunei', 'Bulgaria', 'Cambodia', 'Cameroon', 'Canada', 'Chile', 'China', 'Colombia', 'Costa Rica',
  'Croatia', 'Cyprus', 'Czech Republic', 'Denmark', 'Dominican Republic', 'Ecuador', 'Egypt', 'Estonia', 'Ethiopia', 'Finland',
  'France', 'Georgia', 'Germany', 'Ghana', 'Greece', 'Guatemala', 'Hong Kong', 'Hungary', 'Iceland', 'India', 'Indonesia',
  'Ireland', 'Israel', 'Italy', 'Japan', 'Jordan', 'Kazakhstan', 'Kenya', 'Kuwait', 'Latvia', 'Lebanon', 'Lithuania',
  'Luxembourg', 'Malaysia', 'Maldives', 'Malta', 'Mauritius', 'Mexico', 'Moldova', 'Monaco', 'Mongolia', 'Montenegro',
  'Morocco', 'Myanmar', 'Nepal', 'Netherlands', 'New Zealand', 'Nigeria', 'North Macedonia', 'Norway', 'Oman', 'Pakistan',
  'Panama', 'Peru', 'Philippines', 'Poland', 'Portugal', 'Qatar', 'Romania', 'Saudi Arabia', 'Serbia', 'Singapore',
  'Slovakia', 'Slovenia', 'South Africa', 'South Korea', 'Spain', 'Sri Lanka', 'Sweden', 'Switzerland', 'Taiwan', 'Thailand',
  'Turkey', 'Ukraine', 'United Arab Emirates', 'United Kingdom', 'United States', 'Uruguay', 'Uzbekistan', 'Vietnam', 'Other',
] as const;

const stories = [
  {
    image: 'https://images.pexels.com/photos/3861960/pexels-photo-3861960.jpeg?auto=compress&cs=tinysrgb&w=1600',
    eyebrow: 'Research with purpose',
    title: 'Explore what is possible before deciding what to build.',
    alt: 'Engineers working together in a modern research and development laboratory',
  },
  {
    image: 'https://images.pexels.com/photos/9242807/pexels-photo-9242807.jpeg?auto=compress&cs=tinysrgb&w=1600',
    eyebrow: 'Engineer with precision',
    title: 'Turn hypotheses into working hardware, software and connected systems.',
    alt: 'Engineers testing an electronic motherboard in a technology laboratory',
  },
  {
    image: 'https://images.pexels.com/photos/3862638/pexels-photo-3862638.jpeg?auto=compress&cs=tinysrgb&w=1600',
    eyebrow: 'Launch with impact',
    title: 'Move from prototype to a product built for the real world.',
    alt: 'Engineer working with technical equipment in a research laboratory',
  },
] as const;

function Reveal({ children, delay = 0, className = '' }: { children: ReactNode; delay?: number; className?: string }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 22 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.16 }}
      transition={{ duration: 0.7, delay, ease: [0.16, 1, 0.3, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

export function HomePage() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [formState, setFormState] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');

  async function submitEnquiry(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFormState('sending');
    const form = event.currentTarget;
    const data = Object.fromEntries(new FormData(form).entries());

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!response.ok) throw new Error('Request failed');
      form.reset();
      setFormState('success');
    } catch {
      setFormState('error');
    }
  }

  return (
    <div className="min-h-screen overflow-x-hidden bg-[#f4f1e9] text-[#24312d]">
      <header className="fixed inset-x-0 top-0 z-50 px-3 pt-3 sm:px-6">
        <nav className="mx-auto flex max-w-7xl items-center justify-between rounded-2xl border border-[#2f3e38]/10 bg-[#fbf9f3]/92 px-4 py-3 shadow-[0_12px_42px_rgba(47,62,56,.06)] backdrop-blur-xl sm:px-5">
          <a href="#top" className="flex items-center gap-3">
            <span className="grid size-9 place-items-center rounded-xl bg-[#355f55] text-sm font-semibold text-[#fffdf8]">E</span>
            <span className="text-[.78rem] font-semibold tracking-[0.2em] text-[#263630]">ELATORSPACE</span>
          </a>
          <div className="hidden items-center gap-7 md:flex">
            <a href="#services" className="nav-link">Services</a>
            <a href="#rd" className="nav-link">R&D</a>
            <a href="#about" className="nav-link">Vision</a>
            <a href="/products" className="nav-link">Products</a>
            <a href="#contact" className="btn-header">Start a project</a>
          </div>
          <button
            type="button"
            aria-label="Toggle menu"
            className="grid size-10 place-items-center rounded-xl border border-[#2f3e38]/10 bg-[#fffdf8]/70 md:hidden"
            onClick={() => setMenuOpen((value) => !value)}
          >
            {menuOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
        </nav>
        {menuOpen && (
          <div className="mx-auto mt-2 max-w-7xl rounded-2xl border border-[#2f3e38]/10 bg-[#fbf9f3]/96 p-3 shadow-xl backdrop-blur-xl md:hidden">
            {['services', 'rd', 'about', 'contact'].map((id) => (
              <a key={id} href={`#${id}`} onClick={() => setMenuOpen(false)} className="block rounded-xl px-4 py-3 text-sm capitalize hover:bg-[#355f55]/5">
                {id === 'rd' ? 'R&D' : id}
              </a>
            ))}
            <a href="/products" className="block rounded-xl px-4 py-3 text-sm hover:bg-[#355f55]/5">Products</a>
          </div>
        )}
      </header>

      <main>
        <section id="top" className="soft-grid relative px-4 pb-14 pt-32 sm:px-6 sm:pt-40 lg:px-8 lg:pb-20">
          <div className="mx-auto max-w-7xl">
            <div className="grid gap-12 lg:grid-cols-[1.05fr_.95fr] lg:items-end">
              <div>
                <motion.p
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-6 inline-flex rounded-full border border-[#355f55]/15 bg-[#fffdf8]/70 px-4 py-2 text-[.68rem] font-semibold uppercase tracking-[0.2em] text-[#355f55]"
                >
                  Innovation · R&D · Engineering
                </motion.p>
                <motion.h1
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.78 }}
                  className="max-w-4xl text-balance text-[clamp(2.85rem,5.35vw,5.45rem)] font-medium leading-[.98] tracking-[-.048em] text-[#263630]"
                >
                  We turn bold ideas into <span className="font-serif font-normal italic text-[#557f73]">engineered reality.</span>
                </motion.h1>
                <motion.p
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.12 }}
                  className="mt-7 max-w-2xl text-[.98rem] leading-7 text-[#67726d] sm:text-[1.05rem]"
                >
                  Elatorspace partners with startups and businesses across R&D, product engineering, AI, IoT, data, consulting and digital experiences to build solutions designed for real-world impact.
                </motion.p>
                <motion.div
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="mt-8 flex flex-col gap-3 sm:flex-row"
                >
                  <a href="#contact" className="btn-primary">Start a conversation <ArrowRight size={15} /></a>
                  <a href="#innovation" className="btn-secondary">See how we innovate</a>
                </motion.div>
              </div>

              <Reveal delay={0.12} className="lg:pb-1">
                <div className="rounded-[2rem] border border-[#33423c]/10 bg-[#faf7ef]/80 p-6 sm:p-8">
                  <p className="text-[.67rem] font-semibold uppercase tracking-[.2em] text-[#74817b]">Built for founders and teams who ask</p>
                  <p className="mt-5 max-w-lg text-2xl font-medium leading-[1.25] tracking-[-.035em] text-[#2d3b35] sm:text-3xl">“Can this be done better, smarter or differently?”</p>
                  <div className="mt-8 grid grid-cols-2 gap-3 text-sm">
                    {['Explore', 'Prototype', 'Engineer', 'Scale'].map((item, index) => (
                      <div key={item} className="rounded-2xl border border-[#33423c]/10 bg-[#fffdf8]/75 px-4 py-4">
                        <span className="text-[.62rem] text-[#8b938e]">0{index + 1}</span>
                        <p className="mt-1 font-medium text-[#33423c]">{item}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </section>

        <section id="innovation" className="overflow-hidden border-y border-[#33423c]/8 bg-[#eee9de] py-5 sm:py-6">
          <div className="innovation-marquee" aria-label="Elatorspace innovation stories">
            {[...stories, ...stories].map((story, index) => (
              <article className="innovation-card" key={`${story.eyebrow}-${index}`}>
                <img src={story.image} alt={index < stories.length ? story.alt : ''} loading={index === 0 ? 'eager' : 'lazy'} />
                <div className="innovation-overlay" />
                <div className="innovation-copy">
                  <p>{story.eyebrow}</p>
                  <h2>{story.title}</h2>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="border-b border-[#33423c]/8 bg-[#f9f6ef] px-4 py-8 sm:px-6 lg:px-8">
          <div className="mx-auto grid max-w-7xl gap-6 md:grid-cols-[.8fr_2fr] md:items-center">
            <p className="text-[.67rem] font-semibold uppercase tracking-[0.2em] text-[#818a85]">Trusted collaborations</p>
            <div className="flex flex-wrap items-center gap-x-9 gap-y-4 text-[1.02rem] font-medium tracking-[-.025em] text-[#34423d] sm:text-lg">
              {clients.map((client) => <span key={client}>{client}</span>)}
              <span className="rounded-full border border-[#355f55]/10 bg-[#e2ebe5] px-4 py-2 text-[.78rem] font-semibold text-[#355f55]">50+ startups supported</span>
            </div>
          </div>
        </section>

        <section id="services" className="px-4 py-24 sm:px-6 sm:py-28 lg:px-8">
          <div className="mx-auto max-w-7xl">
            <Reveal className="grid gap-7 lg:grid-cols-[.8fr_1.2fr] lg:items-end">
              <div>
                <p className="eyebrow">Capabilities</p>
                <h2 className="section-title">From possibility to product.</h2>
              </div>
              <p className="max-w-2xl text-[.98rem] leading-7 text-[#707a75] lg:ml-auto">
                Strategy, research and design work alongside software, hardware, data and AI. We shape the team around the problem, not the other way around.
              </p>
            </Reveal>
            <div className="mt-12 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {services.map(([Icon, title, copy], index) => (
                <Reveal key={title} delay={Math.min(index * 0.035, 0.16)}>
                  <article className="group min-h-[16rem] rounded-[1.6rem] border border-[#33423c]/9 bg-[#faf7ef] p-6 transition duration-500 hover:-translate-y-1 hover:bg-[#fffdf8] hover:shadow-[0_22px_65px_rgba(50,64,57,.08)]">
                    <div className="flex items-start justify-between">
                      <span className="grid size-10 place-items-center rounded-2xl bg-[#e5ece7] text-[#355f55]"><Icon size={18} /></span>
                      <span className="text-[.64rem] text-[#9ba29e]">0{index + 1}</span>
                    </div>
                    <h3 className="mt-10 text-[1.05rem] font-semibold tracking-[-.025em] text-[#2d3b35]">{title}</h3>
                    <p className="mt-3 text-[.84rem] leading-6 text-[#737c77]">{copy}</p>
                  </article>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section id="rd" className="px-4 pb-24 sm:px-6 sm:pb-28 lg:px-8">
          <div className="mx-auto max-w-7xl overflow-hidden rounded-[2.2rem] bg-[#345b52] text-[#fffdf8]">
            <div className="grid lg:grid-cols-[.9fr_1.1fr]">
              <Reveal className="p-7 sm:p-10 lg:p-12">
                <p className="mb-5 text-[.67rem] font-semibold uppercase tracking-[.2em] text-[#c7ddd4]">R&D + startup consulting</p>
                <h2 className="max-w-xl text-balance text-[clamp(2.2rem,4vw,4rem)] font-medium leading-[1.02] tracking-[-.045em]">More than a development partner.</h2>
                <p className="mt-6 max-w-lg text-[.94rem] leading-7 text-white/65">
                  We work alongside founders as an extended innovation team — researching, validating, prototyping and engineering ideas before they become expensive assumptions.
                </p>
              </Reveal>
              <div className="grid gap-px bg-white/10 sm:grid-cols-2">
                {[
                  ['Feasibility & research', 'Explore technical possibilities before committing significant product budget.'],
                  ['Proof of concept', 'Turn uncertain ideas into working demonstrations that teams and investors can evaluate.'],
                  ['Prototype engineering', 'Build software, hardware and connected prototypes around the core hypothesis.'],
                  ['Founder consulting', 'Product, technology and delivery guidance for founders navigating early decisions.'],
                ].map(([title, copy], index) => (
                  <Reveal key={title} delay={index * 0.04} className="bg-[#345b52] p-7 sm:p-8">
                    <Check size={17} className="text-[#c7ddd4]" />
                    <h3 className="mt-8 text-[1rem] font-semibold">{title}</h3>
                    <p className="mt-3 text-[.82rem] leading-6 text-white/60">{copy}</p>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section id="about" className="border-y border-[#33423c]/8 bg-[#faf7ef] px-4 py-24 sm:px-6 sm:py-28 lg:px-8">
          <div className="mx-auto max-w-7xl">
            <Reveal className="mb-10">
              <p className="eyebrow">Why we exist</p>
              <h2 className="section-title max-w-4xl">Imagine. Research. Engineer. Elevate.</h2>
            </Reveal>
            <div className="grid gap-4 lg:grid-cols-2">
              <Reveal>
                <article className="h-full rounded-[1.8rem] border border-[#33423c]/9 bg-[#fffdf8] p-7 sm:p-9">
                  <p className="eyebrow">Vision</p>
                  <p className="mt-6 text-[1.45rem] font-medium leading-[1.4] tracking-[-.03em] text-[#314039] sm:text-[1.7rem]">
                    To become the technology and innovation partner behind ambitious ideas — transforming imagination into experiences, products and intelligent solutions that create lasting impact.
                  </p>
                </article>
              </Reveal>
              <Reveal delay={0.06}>
                <article className="h-full rounded-[1.8rem] border border-[#33423c]/9 bg-[#fffdf8] p-7 sm:p-9">
                  <p className="eyebrow">Mission</p>
                  <p className="mt-6 text-[1.1rem] leading-8 tracking-[-.02em] text-[#65706b] sm:text-[1.2rem]">
                    To help startups and businesses move from idea to impact by combining creativity, engineering and strategic thinking — creating memorable experiences, scalable products, AI, IoT and data-driven solutions, and R&D that turns complex challenges into practical, market-ready innovation.
                  </p>
                </article>
              </Reveal>
            </div>
          </div>
        </section>

        <section className="px-4 py-24 sm:px-6 sm:py-28 lg:px-8">
          <Reveal className="mx-auto max-w-7xl rounded-[2rem] border border-[#33423c]/9 bg-[#e8eee9] p-7 sm:p-10 lg:flex lg:items-end lg:justify-between lg:gap-12">
            <div>
              <p className="eyebrow">Elatorspace products</p>
              <h2 className="mt-4 max-w-3xl text-balance text-[clamp(2.2rem,4vw,4.4rem)] font-medium leading-[1.02] tracking-[-.045em] text-[#2d3b35]">Some of our best ideas are still in the lab.</h2>
              <p className="mt-5 max-w-xl text-[.92rem] leading-7 text-[#69736e]">We are developing our own products alongside client innovation work. The first launches are coming soon.</p>
            </div>
            <a href="/products" className="btn-secondary mt-8 shrink-0 lg:mt-0">Visit products <ArrowRight size={15} /></a>
          </Reveal>
        </section>

        <section id="contact" className="px-4 pb-20 sm:px-6 sm:pb-24 lg:px-8">
          <div className="mx-auto grid max-w-7xl gap-8 rounded-[2.2rem] border border-[#33423c]/9 bg-[#fffdf8] p-6 shadow-[0_24px_75px_rgba(51,66,60,.07)] sm:p-9 lg:grid-cols-[.85fr_1.15fr] lg:p-11">
            <Reveal>
              <p className="eyebrow">Start something</p>
              <h2 className="mt-4 max-w-xl text-balance text-[clamp(2.25rem,4vw,4.2rem)] font-medium leading-[1.03] tracking-[-.045em] text-[#2d3b35]">Have an idea worth exploring?</h2>
              <p className="mt-6 max-w-md text-[.92rem] leading-7 text-[#717b76]">Tell us what you are trying to build, validate or improve. We will start with the problem and work forward.</p>
              <div className="mt-8 flex flex-col gap-3 text-sm">
                <a href="mailto:elatorspace.info@gmail.com" className="font-medium text-[#355f55] hover:underline">elatorspace.info@gmail.com</a>
                <a href="https://wa.me/919448786778" target="_blank" rel="noreferrer" className="inline-flex w-fit items-center gap-2 font-medium text-[#355f55] hover:underline"><MessageCircle size={16} /> WhatsApp +91 94487 86778</a>
              </div>
            </Reveal>

            <Reveal delay={0.06}>
              <form onSubmit={submitEnquiry} className="grid gap-4 sm:grid-cols-2">
                <label className="form-label">Name<input className="field-input" name="name" required minLength={2} /></label>
                <label className="form-label">Email<input className="field-input" name="email" type="email" required /></label>
                <label className="form-label">Mobile number <span className="font-normal text-[#98a09c]">(optional)</span><input className="field-input" name="phone" type="tel" inputMode="tel" minLength={7} maxLength={24} placeholder="+91 98765 43210" /></label>
                <label className="form-label">Company<input className="field-input" name="company" /></label>

                <div className="sm:col-span-2 mt-1 rounded-2xl border border-[#33423c]/8 bg-[#faf8f2] p-4">
                  <p className="text-[.68rem] font-semibold uppercase tracking-[.14em] text-[#718079]">Location / address <span className="font-normal normal-case tracking-normal text-[#98a09c]">(optional)</span></p>
                  <div className="mt-4 grid gap-4 sm:grid-cols-2">
                    <label className="form-label">Country
                      <select className="field-input" name="country" defaultValue="">
                        <option value="">Select country</option>
                        {countries.map((country) => <option key={country} value={country}>{country}</option>)}
                      </select>
                    </label>
                    <label className="form-label">State / Province<input className="field-input" name="state" maxLength={100} /></label>
                    <label className="form-label">City<input className="field-input" name="city" maxLength={100} /></label>
                    <label className="form-label">Address<input className="field-input" name="address" maxLength={300} placeholder="Street / area / building" /></label>
                  </div>
                </div>

                <label className="form-label sm:col-span-2">Tell us about the idea<textarea className="field-input min-h-36 resize-y" name="message" required minLength={10} /></label>
                <div className="sm:col-span-2 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <button disabled={formState === 'sending'} type="submit" className="btn-primary disabled:cursor-wait disabled:opacity-60">{formState === 'sending' ? 'Sending…' : 'Send enquiry'} <ArrowRight size={15} /></button>
                  {formState === 'success' && <p className="text-sm text-[#355f55]">Thank you. We received your enquiry.</p>}
                  {formState === 'error' && <p className="text-sm text-[#9a4f45]">Could not send. Please email elatorspace.info@gmail.com.</p>}
                </div>
              </form>
            </Reveal>
          </div>
        </section>
      </main>

      <footer className="border-t border-[#33423c]/8 px-4 py-8 sm:px-6 lg:px-8">
        <div className="mx-auto flex max-w-7xl flex-col gap-4 text-[.76rem] text-[#7a837f] sm:flex-row sm:items-center sm:justify-between">
          <p>© {new Date().getFullYear()} Elatorspace. Experience · Research · Engineering · Intelligence.</p>
          <div className="flex gap-5"><a href="#top">Back to top</a><a href="/products">Products</a><a href="mailto:elatorspace.info@gmail.com">Email</a></div>
        </div>
      </footer>
    </div>
  );
}
